import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineSearch } from "react-icons/ai";
import { useEffect } from "react";
import { locationsService } from "../../../Services/locations.service";
import { clientService } from "../../../Services/client.service";
import { ToastComponent } from "../../Components/ToastComponent";
import { errorHandling } from "../../../Services/error.handling";
import { formatToE164 } from "../../../Services/string.service";
import { getCompanyIdCookie } from "../../../Utils/companyIdCookie";


const UF_LIST = [
  { id: "AC", name: "Acre" },
  { id: "AL", name: "Alagoas" },
  { id: "AP", name: "Amapá" },
  { id: "AM", name: "Amazonas" },
  { id: "BA", name: "Bahia" },
  { id: "CE", name: "Ceará" },
  { id: "DF", name: "Distrito Federal" },
  { id: "ES", name: "Espírito Santo" },
  { id: "GO", name: "Goiás" },
  { id: "MA", name: "Maranhão" },
  { id: "MT", name: "Mato Grosso" },
  { id: "MS", name: "Mato Grosso do Sul" },
  { id: "MG", name: "Minas Gerais" },
  { id: "PA", name: "Pará" },
  { id: "PB", name: "Paraíba" },
  { id: "PR", name: "Paraná" },
  { id: "PE", name: "Pernambuco" },
  { id: "PI", name: "Piauí" },
  { id: "RJ", name: "Rio de Janeiro" },
  { id: "RN", name: "Rio Grande do Norte" },
  { id: "RS", name: "Rio Grande do Sul" },
  { id: "RO", name: "Rondônia" },
  { id: "RR", name: "Roraima" },
  { id: "SC", name: "Santa Catarina" },
  { id: "SP", name: "São Paulo" },
  { id: "SE", name: "Sergipe" },
  { id: "TO", name: "Tocantins" },
];

const stripAccents = (value) =>
  value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// O select de UF só marca a opção certa se o value bater exatamente com um
// UF_LIST.id (sigla de 2 letras maiúsculas) — se a API (GET /customers/{id}
// ou a busca por CEP) devolver o nome completo do estado, minúsculo, ou com
// espaço sobrando, o select fica sem nada selecionado ("zoado"). Normaliza
// pra sigla nos dois casos antes de guardar no state.
const normalizeUf = (value) => {
  if (!value) return "";
  const raw = value.toString().trim();
  if (raw.length === 2) {
    const upper = raw.toUpperCase();
    return UF_LIST.some((uf) => uf.id === upper) ? upper : raw;
  }
  const match = UF_LIST.find((uf) => stripAccents(uf.name) === stripAccents(raw));
  return match ? match.id : raw;
};

const EMPTY_ADDRESS = {
  cep: "",
  address: "",
  number: "",
  neighborhood: "",
  complement: "",
  uf: "",
  city: "",
};


export default function Address({
  clientId,
  isNewCustomer,
  mainAddress,
  billingAddress,
  sameAsMain,
  onMainAddressChange,
  onBillingAddressChange,
  onSameAsMainChange,
  addressLoadedClientId,
  onAddressLoaded,
  onAddressSaved,
}) {
  const { t } = useTranslation();

  const [isSearchingCep, setIsSearchingCep] = useState({ main: false, billing: false });
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toastParams, setToastParams] = useState("");

  // Duas tentativas de bater a altura do select de UF via fórmula CSS
  // (min-height, depois height + box-sizing) não resolveram — deve ser
  // algum comportamento nativo do <select> que a fórmula não cobre. Em vez
  // de adivinhar de novo, mede a altura real de um <input> de referência já
  // renderizado e aplica esse valor exato, em px, no select.
  const referenceInputRef = useRef(null);
  const [inputHeight, setInputHeight] = useState(null);

  useEffect(() => {
    if (referenceInputRef.current) {
      setInputHeight(referenceInputRef.current.getBoundingClientRect().height);
    }
  }, []);

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  // Carrega o endereço do cliente a partir do GET /customers/{id} — os
  // campos de endereço vêm "achatados" na resposta (Address1/2, Number1/2,
  // District1/2, Complement1/2, City1/2, State1/2, PostalCode1/2), sem
  // endpoint próprio de endereço (confirmado no swagger em 28/08/2026).
  useEffect(() => {
    if (!clientId || isNewCustomer) {
      onMainAddressChange(EMPTY_ADDRESS);
      onBillingAddressChange(EMPTY_ADDRESS);
      onSameAsMainChange(false);
      onAddressLoaded(null);
      return;
    }

    if (clientId === addressLoadedClientId) {
      return;
    }

    let isCurrent = true;

    const loadClientAddress = async () => {
      setIsLoadingClient(true);
      try {
        const client = await clientService.fetchClient(clientId);
        if (!isCurrent || !client) return;

        const main = {
          cep: client.postalCode1 || "",
          address: client.address1 || "",
          number: client.number1 || "",
          neighborhood: client.district1 || "",
          complement: client.complement1 || "",
          city: client.city1 || "",
          uf: normalizeUf(client.state1),
        };
        const billing = {
          cep: client.postalCode2 || "",
          address: client.address2 || "",
          number: client.number2 || "",
          neighborhood: client.district2 || "",
          complement: client.complement2 || "",
          city: client.city2 || "",
          uf: normalizeUf(client.state2),
        };

        onMainAddressChange(main);
        onBillingAddressChange(billing);
      } catch (error) {
        if (isCurrent) {
          showToast(t("COMMON.ERROR"), t("CUSTOMER.ADDRESS.LOAD_ERROR"), "Danger");
        }
      } finally {
        if (isCurrent) setIsLoadingClient(false);
      }
    };

    loadClientAddress();

    return () => {
      isCurrent = false;
    };
  }, [
    clientId,
    isNewCustomer,
    addressLoadedClientId,
    onAddressLoaded,
    onBillingAddressChange,
    onMainAddressChange,
    onSameAsMainChange,
    t,
  ]);

  const handleFieldChange = (target, field) => (e) => {
    const { value } = e.target;
    if (target === "main") {
      onMainAddressChange((prev) => ({ ...prev, [field]: value }));
      if (sameAsMain) {
        onBillingAddressChange((prev) => ({ ...prev, [field]: value }));
      }
    } else {
      onBillingAddressChange((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleCepSearch = async (target) => {
    const current = target === "main" ? mainAddress : billingAddress;
    const cep = current.cep.replace(/\D/g, "");

    if (cep.length !== 8) {
      showToast(t("COMMON.ATTENTION"), t("CUSTOMER.ADDRESS.INVALID_CEP"), "Warning");
      return;
    }

    setIsSearchingCep((prev) => ({ ...prev, [target]: true }));

    try {
      const result = await locationsService.searchByCep(cep);

      if (!result) {
        showToast(t("COMMON.ATTENTION"), t("CUSTOMER.ADDRESS.CEP_NOT_FOUND"), "Danger");
        return;
      }

      const updated = {
        ...current,
        cep,
        address: result.address || "",
        number: result.addressNumber || current.number,
        complement: result.addressComplement || current.complement,
        neighborhood: result.addressNeighborhood || "",
        city: result.addressCity || "",
        uf: normalizeUf(result.addressState),
      };

      showToast(t("COMMON.SUCCESS"), t("CUSTOMER.ADDRESS.CEP_FOUND"), "Success");

      if (target === "main") {
        onMainAddressChange(updated);
        if (sameAsMain) onBillingAddressChange(updated);
      } else {
        onBillingAddressChange(updated);
      }
    } catch (error) {
      showToast(t("COMMON.ERROR"), t("CUSTOMER.ADDRESS.CEP_SEARCH_ERROR"), "Danger");
    } finally {
      setIsSearchingCep((prev) => ({ ...prev, [target]: false }));
    }
  };

  const handleSameAsMainToggle = (e) => {
    const checked = e.target.checked;
    onSameAsMainChange(checked);
    if (checked) {
      onBillingAddressChange(mainAddress);
    }
  };

  // Monta o payload COMPLETO exigido pelo PUT /customers/{id} a partir do
  // cliente recém-buscado (mesmo motivo do Notes.jsx: o endpoint não aceita
  // atualização parcial). Os únicos campos que divergem do que está salvo
  // no backend são os de endereço, que vêm do state local (main/billing)
  // já editado na tela — os demais campos são só "passados adiante" com o
  // nome de campo do PUT corrigido em relação ao GET (igual ao mapeamento
  // feito no PersonalData.jsx).
  const buildFullPayload = (client) => ({
    RegionalId: getCompanyIdCookie(),
    ClientName: client.clientName ?? "",
    ClientTradeName: client.clientTradeName ?? "",
    Doc: client.doc ?? "",
    RepresentativeId: client.representativeId || null,
    SellerId: client.salespersonId || null,
    ServicePlanId: client.servicePlanId || null,
    MasterUserEmail: null,
    MasterUserName: null,
    MasterUserAccessLevelId: null,
    StateRegistration: client.stateRegistration ?? "",
    MunicipalRegistration: client.municipalRegistration ?? "",
    SimpleTax: client.simpleOption ? "S" : "N",
    Public: null,
    Email: client.responsibleEmail || null,
    PhoneNumber: formatToE164(client.phone1 ?? ""),
    SecondaryPhoneNumber: formatToE164(client.phone2 ?? ""),
    Observation: null,
    OperationalObservation: null,
    BillingObservation: null,
    BirthDate: client.birthDate || null,
    PersonType: client.personType ?? "",
    Dda: client.dda ?? "",
    CreditAnalysis: client.creditAnalysis ?? "",
    CreditAnalysisDate: null,
    PaymentBook: client.paymentBook ?? "",
    AdministrativeRestrictionId: client.administrativeRestrictionId || null,
    AdministrativeRestrictionDate: client.administrativeRestrictionDate || null,
    AdministrativeRestrictionEndDate: client.administrativeRestrictionEndDate || null,
    LastTemporarySuspensionDate: null,
    AuthorizeEmail: null,
    AuthorizeSms: null,
    AuthorizeNegativeList: null,
    AuthorizeAutomaticRestriction: null,
    Whatsapp: null,
    // A partir daqui: dados de endereço, vindos do state local editado
    // nesta aba (não do client recém-buscado).
    ZipCode: mainAddress?.cep?.replace(/\D/g, "") || "",
    Street: mainAddress?.address || "",
    Number: mainAddress?.number || "",
    Neighborhood: mainAddress?.neighborhood || "",
    Complement: mainAddress?.complement || "",
    City: mainAddress?.city || "",
    State: mainAddress?.uf || "",
    SecondaryZipCode: billingAddress?.cep?.replace(/\D/g, "") || "",
    SecondaryStreet: billingAddress?.address || "",
    SecondaryNumber: billingAddress?.number || "",
    SecondaryNeighborhood: billingAddress?.neighborhood || "",
    SecondaryComplement: billingAddress?.complement || "",
    SecondaryCity: billingAddress?.city || "",
    SecondaryState: billingAddress?.uf || "",
  });

  const handleSaveAddress = async () => {
    if (!clientId || isNewCustomer) return;

    setIsSaving(true);
    try {
      const client = await clientService.fetchClient(clientId);
      if (!client || client.error) {
        showToast(t("COMMON.ERROR"), t("CUSTOMER.ADDRESS.LOAD_ERROR"), "Danger");
        return;
      }

      const payload = buildFullPayload(client);
      const result = await clientService.updateClient(clientId, payload);

      if (!result || result.error) {
        const backendMessage = errorHandling.getErrorMessage(
          result,
          t("CUSTOMER.ADDRESS.SAVE_ERROR")
        );
        showToast(t("COMMON.ERROR"), backendMessage, "Danger");
        return;
      }

      showToast(t("COMMON.SUCCESS"), t("CUSTOMER.ADDRESS.SAVED_SUCCESS"), "Success");
      onAddressSaved?.(clientId);
    } catch (error) {
      showToast(t("COMMON.ERROR"), t("CUSTOMER.ADDRESS.SAVE_ERROR"), "Danger");
    } finally {
      setIsSaving(false);
    }
  };

  const renderInput = (target, field, label, values, disabled, colClass, customName, inputRef) => (
    <div className={colClass}>
      <label className="form-label small text-uppercase text-muted mb-1">{label}</label>
      <input
        ref={inputRef}
        type="text"
        className="form-control form-control-sm"
        name={customName || `${target}-${field}`}
        value={values[field]}
        onChange={handleFieldChange(target, field)}
        disabled={disabled}
        autoComplete="off"
      />
    </div>
  );

  const renderSelect = (target, field, label, values, disabled, colClass, options, getOptionLabel = (opt) => opt.name) => (
    <div className={colClass}>
      <label className="form-label small text-uppercase text-muted mb-1">{label}</label>
      <select
        className="form-select form-select-sm"
        // Fórmula CSS (min-height, depois height) não bateu com a altura
        // real do <input> vizinho — deve ser comportamento nativo do
        // <select>. Em vez de adivinhar de novo, usa a altura medida de um
        // input de referência (inputHeight, via referenceInputRef) assim
        // que ela estiver disponível; até lá, cai de volta na fórmula.
        style={{
          height: inputHeight ? `${inputHeight}px` : "calc(1.53em + 0.5rem + 2px)",
          boxSizing: "border-box",
        }}
        name={`${target}-${field}`}
        value={values[field]}
        onChange={handleFieldChange(target, field)}
        disabled={disabled}
        autoComplete="off"
      >
        <option value="">--{label}--</option>
        {options.map((opt) => (
          <option key={opt.id} value={opt.id} title={opt.name}>
            {getOptionLabel(opt)}
          </option>
        ))}
      </select>
    </div>
  );

  const renderAddressBlock = (target, values, disabled) => (
    <>
      <div className="row g-2 align-items-end mb-2">
        {renderInput(target, "cep", t("CUSTOMER.ADDRESS.CEP"), values, disabled, "col-md-3", `${target}-postal-code-search`)}
        <div className="col-md-auto">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => handleCepSearch(target)}
            disabled={disabled || isSearchingCep[target]}
          >
            <AiOutlineSearch className="icon-in-button" />
            {t("COMMON.SEARCH")}
          </button>
        </div>
      </div>

      <div className="row g-2 mb-2">
        {renderInput(target, "address", t("CUSTOMER.ADDRESS.STREET"), values, disabled, "col-md-5")}
        {renderInput(target, "number", t("CUSTOMER.ADDRESS.NUMBER"), values, disabled, "col-md-2")}
        {renderInput(target, "neighborhood", t("CUSTOMER.ADDRESS.NEIGHBORHOOD"), values, disabled, "col-md-5")}
      </div>

      <div className="row g-2">
        {renderInput(
          target,
          "complement",
          t("CUSTOMER.ADDRESS.COMPLEMENT"),
          values,
          disabled,
          "col-md-5",
          undefined,
          target === "main" ? referenceInputRef : undefined
        )}
        {renderSelect(target, "uf", t("CUSTOMER.ADDRESS.UF"), values, disabled, "col-md-2", UF_LIST, (opt) => opt.id)}
        {renderInput(target, "city", t("CUSTOMER.ADDRESS.CITY"), values, disabled, "col-md-5")}
      </div>
    </>
  );

  return (
    <div className="p-3">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <div className="bg-primary p-2 rounded-top mb-2">
        <strong style={{ color: "var(--text-primary)" }}>
          {t("CUSTOMER.ADDRESS.MAIN_TITLE")}
        </strong>
      </div>
      {renderAddressBlock("main", mainAddress, false)}

      <div className="bg-primary p-2 rounded-top mt-4 mb-2">
        <strong style={{ color: "var(--text-primary)" }}>
          {t("CUSTOMER.ADDRESS.BILLING_TITLE")}
        </strong>
      </div>

      <div className="form-check mb-2">
        <input
          type="checkbox"
          className="form-check-input"
          id="sameAsMainAddress"
          checked={sameAsMain}
          onChange={handleSameAsMainToggle}
        />
        <label className="form-check-label" htmlFor="sameAsMainAddress">
          {t("CUSTOMER.ADDRESS.SAME_AS_MAIN")}
        </label>
      </div>

      {renderAddressBlock("billing", billingAddress, sameAsMain)}

      <div className="d-flex justify-content-end mt-3">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSaveAddress}
          disabled={isSaving || isLoadingClient || !clientId || isNewCustomer}
        >
          {isSaving ? t("CUSTOMER.ADDRESS.SAVING") : t("COMMON.SAVE")}
        </button>
      </div>
    </div>
  );
}
